// CRM.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './CRM.css';
import '../databases/databases.css';
import Navbar from '../../shared/navbar.js';

// ─── Constants ────────────────────────────────────────────────────────────────
const PIPELINE_STAGES = [
  { key: ['new'], label: 'New Leads', color: '#3b82f6' },
  { key: ['contacted', 'qualified'], label: 'Qualified', color: '#10b981' },
  { key: ['viewing'], label: 'Viewing', color: '#f59e0b' },
  { key: ['application', 'background', 'screening', 'review'], label: 'Application', color: '#8b5cf6' },
  { key: ['approved', 'lease_signed', 'move_in', 'moved_in', 'closed_won'], label: 'Lease Signed', color: '#059669' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'Inquiry' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'viewing', label: 'Viewing' },
  { value: 'application', label: 'Application' },
  { value: 'background', label: 'Background Check' },
  { value: 'screening', label: 'Screening' },
  { value: 'review', label: 'Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'lease_signed', label: 'Lease Signed' },
  { value: 'move_in', label: 'Move In' },
  { value: 'moved_in', label: 'Moved In' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'rejected', label: 'Rejected' },
];

const SOURCE_OPTIONS = ['All', 'Website', 'Referral', 'Social Media', 'Direct Contact', 'Email Campaign', 'Open House'];

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Phone Call', icon: '📞' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'tour', label: 'Tour / Showing', icon: '🏠' },
  { value: 'note', label: 'Note', icon: '📝' },
  { value: 'meeting', label: 'Meeting', icon: '🤝' },
];

export default function CRM() {
  // ─── State ───────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('prospects');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [activities, setActivities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [statusFilters, setStatusFilters] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('All');
  const [scoreMin, setScoreMin] = useState('');
  const [scoreMax, setScoreMax] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [logActivity, setLogActivity] = useState({
    leadId: '', type: 'note', description: '', date: new Date().toISOString().split('T')[0]
  });
  const [newLead, setNewLead] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    source: 'Website', score: 85, status: 'new',
    notes: '', propertyInterest: '', budgetMin: '', budgetMax: '', moveInDate: ''
  });
  const navigate = useNavigate();

  useEffect(() => { loadCRMData(); }, []);

  // ─── Data Loading ─────────────────────────────────────────────────────────────
  const loadCRMData = async () => {
    try {
      setLoading(true);
      const [applicantsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/applicants`),
      ]);
      const applicantsData = applicantsRes.ok ? await applicantsRes.json() : [];

      const leadsData = applicantsData.map((a) => ({
        id: a.id,
        firstName: a.first_name || '',
        lastName: a.last_name || '',
        email: a.email || '',
        phone: a.phone || '',
        source: a.source || 'Website',
        score: a.lead_score ?? 85,
        status: a.status || 'new',
        notes: a.notes || '',
        propertyInterest: a.property_interest || '',
        budgetMin: a.budget_min ?? null,
        budgetMax: a.budget_max ?? null,
        moveInDate: a.move_in_date ?? null,
        assignedAgent: a.assigned_agent || 'Unassigned',
        lastContact: a.last_contact ?? null,
        createdAt: a.created_at || new Date().toISOString(),
      }));
      setLeads(leadsData);

      // Build activity feed from notes & last_contact
      const activitiesData = [];
      leadsData.forEach((lead) => {
        if (lead.lastContact) {
          activitiesData.push({
            id: `contact-${lead.id}`,
            leadId: lead.id,
            type: 'contact',
            description: `Contacted ${lead.firstName} ${lead.lastName}`,
            contact: `${lead.firstName} ${lead.lastName}`,
            date: lead.lastContact,
            status: 'completed',
          });
        }
        if (lead.notes) {
          lead.notes.split('\n').filter(Boolean).slice(-3).forEach((note, i) => {
            const typeMatch = note.match(/\[(.*?)\]\s*([\w\s]+):/);
            activitiesData.push({
              id: `note-${lead.id}-${i}`,
              leadId: lead.id,
              type: typeMatch ? typeMatch[2].toLowerCase().includes('call') ? 'call'
                : typeMatch[2].toLowerCase().includes('email') ? 'email'
                : typeMatch[2].toLowerCase().includes('tour') ? 'tour'
                : 'note' : 'note',
              description: note.length > 120 ? note.slice(0, 120) + '…' : note,
              contact: `${lead.firstName} ${lead.lastName}`,
              date: typeMatch?.[1] || lead.createdAt?.split('T')[0] || '',
              status: 'completed',
            });
          });
        }
      });
      activitiesData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setActivities(activitiesData.slice(0, 40));

      // Pipeline kanban
      const pipelineData = PIPELINE_STAGES.map((stage) => {
        const stageLeads = leadsData.filter((l) => stage.key.includes(l.status));
        const totalValue = stageLeads.reduce((sum, l) => sum + (l.budgetMax || l.budgetMin || 1500), 0);
        return { stage: stage.label, color: stage.color, count: stageLeads.length, value: `$${totalValue.toLocaleString()}`, leads: stageLeads };
      });
      setPipeline(pipelineData);
    } catch (err) {
      console.error('Error loading CRM data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Quick Status Update ──────────────────────────────────────────────────────
  const handleStatusChange = async (leadId, newStatus, e) => {
    e && e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE_URL}/api/applicants/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, status: newStatus } : l));
      }
    } catch (err) { console.error('Status update failed:', err); }
  };

  // ─── Add Lead ─────────────────────────────────────────────────────────────────
  const handleAddLead = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/applicants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: newLead.firstName, last_name: newLead.lastName,
          email: newLead.email, phone: newLead.phone,
          source: newLead.source, status: newLead.status,
          lead_score: newLead.score, notes: newLead.notes,
          property_interest: newLead.propertyInterest,
          budget_min: newLead.budgetMin ? parseFloat(newLead.budgetMin) : null,
          budget_max: newLead.budgetMax ? parseFloat(newLead.budgetMax) : null,
          move_in_date: newLead.moveInDate || null,
          last_contact: new Date().toISOString().split('T')[0],
        }),
      });
      if (res.ok) {
        await loadCRMData();
        setShowAddModal(false);
        setNewLead({ firstName: '', lastName: '', email: '', phone: '', source: 'Website', score: 85, status: 'new', notes: '', propertyInterest: '', budgetMin: '', budgetMax: '', moveInDate: '' });
      }
    } catch (err) { console.error('Error creating lead:', err); }
  };

  // ─── Log Activity ─────────────────────────────────────────────────────────────
  const handleLogActivity = async () => {
    if (!logActivity.leadId || !logActivity.description) return;
    try {
      const lead = leads.find((l) => l.id === parseInt(logActivity.leadId));
      if (!lead) return;
      const typeLabel = ACTIVITY_TYPES.find((t) => t.value === logActivity.type)?.label || logActivity.type;
      const newEntry = `[${logActivity.date}] ${typeLabel}: ${logActivity.description}`;
      const updatedNotes = lead.notes ? `${lead.notes}\n${newEntry}` : newEntry;
      const res = await fetch(`${API_BASE_URL}/api/applicants/${logActivity.leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: updatedNotes, last_contact: logActivity.date }),
      });
      if (res.ok) {
        await loadCRMData();
        setShowLogActivity(false);
        setLogActivity({ leadId: '', type: 'note', description: '', date: new Date().toISOString().split('T')[0] });
      }
    } catch (err) { console.error('Log activity failed:', err); }
  };

  // ─── Bulk Actions ─────────────────────────────────────────────────────────────
  const handleBulkStatusChange = async () => {
    if (!bulkStatus || selectedLeads.size === 0) return;
    try {
      await Promise.all([...selectedLeads].map((id) =>
        fetch(`${API_BASE_URL}/api/applicants/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: bulkStatus }),
        })
      ));
      await loadCRMData();
      setSelectedLeads(new Set());
      setBulkStatus('');
    } catch (err) { console.error('Bulk status update failed:', err); }
  };

  // ─── CSV Export ───────────────────────────────────────────────────────────────
  const exportCSV = (data, filename) => {
    const headers = ['Name', 'Email', 'Phone', 'Status', 'Source', 'Score', 'Budget Min', 'Budget Max', 'Property Interest', 'Move-in Date', 'Last Contact', 'Created At'];
    const rows = data.map((l) => [
      `${l.firstName} ${l.lastName}`, l.email, l.phone,
      getStatusLabel(l.status), l.source, l.score,
      l.budgetMin || '', l.budgetMax || '', l.propertyInterest || '',
      l.moveInDate || '', l.lastContact || '', l.createdAt?.split('T')[0] || '',
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const getInitials = (f, l) => `${(f || '').charAt(0).toUpperCase()}${(l || '').charAt(0).toUpperCase()}` || '??';
  const parseUnitType = (p) => {
    if (!p) return 'N/A';
    const m = p.match(/(\d+)\s*(?:bed|br|bdr|bedroom)/i);
    if (m) return `${m[1]}BR`;
    if (p.toLowerCase().includes('studio')) return 'Studio';
    return p.length > 12 ? p.slice(0, 12) + '…' : p;
  };
  const getStatusLabel = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || 'Inquiry';
  const getStatusBadgeClass = (s) => {
    const map = {
      new: 'inquiry', contacted: 'contacted', qualified: 'qualified', viewing: 'toured',
      application: 'application', background: 'background', screening: 'screening',
      review: 'review', proposal: 'proposal', negotiation: 'negotiation',
      approved: 'approved', lease_signed: 'lease-signed', move_in: 'move-in',
      moved_in: 'moved-in', closed_won: 'closed-won', closed_lost: 'closed-lost', rejected: 'rejected',
    };
    return `status-badge ${map[s] || 'inquiry'}`;
  };
  const getSourceIcon = (s) => {
    const icons = { Website: '🌐', Referral: '👥', 'Social Media': '📱', 'Direct Contact': '📞', 'Email Campaign': '📧', 'Open House': '🏠' };
    return icons[s] || '📋';
  };
  const getActivityIcon = (t) => ACTIVITY_TYPES.find((a) => a.value === t)?.icon || '📝';
  const getScoreColor = (score) => score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';

  // ─── Filtering + Sorting ─────────────────────────────────────────────────────
  let filteredLeads = leads.filter((lead) => {
    const q = searchTerm.toLowerCase();
    const searchMatch = !searchTerm ||
      `${lead.firstName} ${lead.lastName}`.toLowerCase().includes(q) ||
      (lead.email || '').toLowerCase().includes(q) ||
      (lead.phone || '').toLowerCase().includes(q) ||
      (lead.propertyInterest || '').toLowerCase().includes(q);
    const statusMatch = statusFilters.length === 0 || statusFilters.includes(lead.status);
    const sourceMatch = sourceFilter === 'All' || lead.source === sourceFilter;
    const scoreMinMatch = !scoreMin || lead.score >= parseInt(scoreMin);
    const scoreMaxMatch = !scoreMax || lead.score <= parseInt(scoreMax);
    return searchMatch && statusMatch && sourceMatch && scoreMinMatch && scoreMaxMatch;
  });

  filteredLeads = [...filteredLeads].sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.createdAt) - new Date(a.createdAt);
      case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
      case 'score-high': return (b.score || 0) - (a.score || 0);
      case 'score-low': return (a.score || 0) - (b.score || 0);
      case 'name-az': return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      case 'name-za': return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
      case 'status': return (a.status || '').localeCompare(b.status || '');
      case 'last-contact': return new Date(b.lastContact || 0) - new Date(a.lastContact || 0);
      default: return 0;
    }
  });

  const totalPages = Math.ceil(filteredLeads.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, startIndex + rowsPerPage);
  const activeFilterCount = (statusFilters.length > 0 ? 1 : 0) + (sourceFilter !== 'All' ? 1 : 0) + (scoreMin || scoreMax ? 1 : 0);

  // KPIs
  const activeLeads = leads.filter((l) => ['new', 'qualified', 'contacted', 'viewing'].includes(l.status)).length;
  const toursScheduled = leads.filter((l) => l.status === 'viewing').length;
  const applications = leads.filter((l) => ['application', 'background', 'screening', 'review'].includes(l.status)).length;
  const conversionRate = leads.length > 0
    ? Math.round((leads.filter((l) => ['lease_signed', 'approved', 'moved_in', 'closed_won'].includes(l.status)).length / leads.length) * 100)
    : 0;

  // Selection helpers
  const toggleSelect = (id, e) => {
    e && e.stopPropagation();
    setSelectedLeads((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    setSelectedLeads(selectedLeads.size === paginatedLeads.length && paginatedLeads.length > 0
      ? new Set()
      : new Set(paginatedLeads.map((l) => l.id))
    );
  };

  if (loading) {
    return (
      <div className="crm-container">
        <Navbar selected="CRM" />
        <div className="main-content">
          <div className="content-area">
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading CRM...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="crm-container">
      <Navbar selected="CRM" />
      <div className="main-content">
        <div className="content-area">

          {/* ── Header ────────────────────────────────────────────────────────── */}
          <section className="crm-header">
            <div className="header-left">
              <h1 className="crm-title">CRM</h1>
              <p className="crm-subtitle">Manage leads, tenants, and the leasing pipeline.</p>
            </div>
            <div className="header-right">
              <button className="btn-secondary" onClick={() => exportCSV(filteredLeads, 'crm-leads.csv')}>
                ↓ Export CSV
              </button>
              <button className="add-contact-btn" onClick={() => setShowAddModal(true)}>
                <span className="plus-icon">+</span> Add Lead
              </button>
            </div>
          </section>

          {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
          <section className="crm-kpi-cards">
            <div className="kpi-card">
              <div className="kpi-icon active-leads-icon">👥</div>
              <div className="kpi-content">
                <div className="kpi-label">Active Leads</div>
                <div className="kpi-value">{activeLeads}</div>
                <div className="kpi-trend positive"><span className="trend-arrow">↗</span> +12%</div>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon tours-icon">📅</div>
              <div className="kpi-content">
                <div className="kpi-label">Tours Scheduled</div>
                <div className="kpi-value">{toursScheduled}</div>
                <div className="kpi-trend positive"><span className="trend-arrow">↗</span> +5%</div>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon applications-icon">📋</div>
              <div className="kpi-content">
                <div className="kpi-label">Applications</div>
                <div className="kpi-value">{applications}</div>
                <div className="kpi-trend positive"><span className="trend-arrow">↗</span> +18%</div>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon conversion-icon">📊</div>
              <div className="kpi-content">
                <div className="kpi-label">Conversion Rate</div>
                <div className="kpi-value">{conversionRate}%</div>
                <div className="kpi-trend positive"><span className="trend-arrow">↗</span> +3%</div>
              </div>
            </div>
          </section>

          {/* ── Navigation Tabs ───────────────────────────────────────────────── */}
          <section className="crm-tabs">
            {[
              { key: 'prospects', icon: '⭐', label: 'Prospects' },
              { key: 'pipeline', icon: '📊', label: 'Pipeline' },
              { key: 'activity', icon: '🕐', label: 'Activity' },
            ].map((t) => (
              <button
                key={t.key}
                className={`tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                <span className="tab-icon">{t.icon}</span>{t.label}
              </button>
            ))}
          </section>

          {/* ── Toolbar (Search + Sort + Filter + View Toggle) ────────────────── */}
          {activeTab === 'prospects' && (
            <>
              <section className="crm-toolbar">
                <div className="toolbar-left">
                  <div className="search-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      placeholder="Search name, email, phone..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      className="crm-search-input"
                    />
                    {searchTerm && (
                      <button className="search-clear" onClick={() => setSearchTerm('')}>×</button>
                    )}
                  </div>

                  {activeTab === 'prospects' && (
                    <select
                      className="sort-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="score-high">Score: High → Low</option>
                      <option value="score-low">Score: Low → High</option>
                      <option value="name-az">Name: A → Z</option>
                      <option value="name-za">Name: Z → A</option>
                      <option value="status">Status</option>
                      <option value="last-contact">Last Contact</option>
                    </select>
                  )}

                  {activeTab === 'prospects' && (
                    <button
                      className={`filter-btn ${showFilterPanel ? 'filter-btn-active' : ''}`}
                      onClick={() => setShowFilterPanel((p) => !p)}
                    >
                      🔽 Filter
                      {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
                    </button>
                  )}
                </div>

                <div className="toolbar-right">
                  {activeTab === 'prospects' && (
                    <span className="results-count">{filteredLeads.length} prospect{filteredLeads.length !== 1 ? 's' : ''}</span>
                  )}
                  <div className="view-toggle">
                    <button
                      className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      ⊞
                    </button>
                    <button
                      className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setViewMode('list')}
                      title="List View"
                    >
                      ☰
                    </button>
                  </div>
                </div>
              </section>

              {/* Collapsible Filter Panel */}
              {activeTab === 'prospects' && showFilterPanel && (
                <section className="filter-panel">
                  <div className="filter-panel-inner">
                    <div className="filter-group">
                      <label className="filter-label">Status</label>
                      <div className="status-chips">
                        {STATUS_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            className={`chip ${statusFilters.includes(opt.value) ? 'active' : ''}`}
                            onClick={() => {
                              setStatusFilters((prev) =>
                                prev.includes(opt.value)
                                  ? prev.filter((s) => s !== opt.value)
                                  : [...prev, opt.value]
                              );
                              setCurrentPage(1);
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="filter-row">
                      <div className="filter-group">
                        <label className="filter-label">Source</label>
                        <select
                          className="filter-select"
                          value={sourceFilter}
                          onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }}
                        >
                          {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="filter-group">
                        <label className="filter-label">Lead Score Range</label>
                        <div className="score-range">
                          <input
                            type="number" min="0" max="100" placeholder="Min"
                            value={scoreMin}
                            onChange={(e) => setScoreMin(e.target.value)}
                            className="score-input"
                          />
                          <span className="score-dash">–</span>
                          <input
                            type="number" min="0" max="100" placeholder="Max"
                            value={scoreMax}
                            onChange={(e) => setScoreMax(e.target.value)}
                            className="score-input"
                          />
                        </div>
                      </div>
                      <div className="filter-group filter-clear-col">
                        <button
                          className="btn-secondary"
                          onClick={() => { setStatusFilters([]); setSourceFilter('All'); setScoreMin(''); setScoreMax(''); }}
                        >
                          Clear All Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </>
          )}

          {/* ── Bulk Action Bar ───────────────────────────────────────────────── */}
          {selectedLeads.size > 0 && (
            <div className="bulk-bar">
              <span className="bulk-count">{selectedLeads.size} selected</span>
              <select
                className="sort-select"
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
              >
                <option value="">Change Status…</option>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                className="btn-primary btn-sm"
                onClick={handleBulkStatusChange}
                disabled={!bulkStatus}
              >
                Apply
              </button>
              <button
                className="btn-secondary btn-sm"
                onClick={() => exportCSV(leads.filter((l) => selectedLeads.has(l.id)), 'selected-leads.csv')}
              >
                ↓ Export Selected
              </button>
              <button className="btn-secondary btn-sm" onClick={() => setSelectedLeads(new Set())}>
                Clear
              </button>
            </div>
          )}

          {/* ── Content ───────────────────────────────────────────────────────── */}
          <section className="crm-content">

            {/* ── Prospects ─────────────────────────────────────────────────── */}
            {activeTab === 'prospects' && (
              <>
                {paginatedLeads.length === 0 ? (
                  <div className="empty-state">
                    <p>No prospects found. Try adjusting your filters or click "Add Lead".</p>
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className="prospects-grid">
                    {paginatedLeads.map((lead) => (
                      <div
                        key={lead.id}
                        className="prospect-card"
                        onClick={() => navigate(`/applicant/${lead.id}`)}
                      >
                        <div className="prospect-card-header">
                          <input
                            type="checkbox"
                            className="card-checkbox"
                            checked={selectedLeads.has(lead.id)}
                            onChange={(e) => toggleSelect(lead.id, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="prospect-avatar">{getInitials(lead.firstName, lead.lastName)}</div>
                          <div className="prospect-header-info">
                            <span className="prospect-name-link">{lead.firstName} {lead.lastName}</span>
                            <span className={getStatusBadgeClass(lead.status)}>{getStatusLabel(lead.status)}</span>
                          </div>
                        </div>

                        {/* Lead Score Bar */}
                        <div className="score-bar-wrapper">
                          <span className="score-label">Score</span>
                          <div className="score-bar-track">
                            <div
                              className="score-bar-fill"
                              style={{ width: `${lead.score}%`, background: getScoreColor(lead.score) }}
                            />
                          </div>
                          <span className="score-value" style={{ color: getScoreColor(lead.score) }}>
                            {lead.score}
                          </span>
                        </div>

                        <div className="prospect-contact-info">
                          <div className="contact-info-item">
                            <span className="contact-icon">📧</span>
                            <span className="contact-text">{lead.email}</span>
                          </div>
                          {lead.phone && (
                            <div className="contact-info-item">
                              <span className="contact-icon">📞</span>
                              <span className="contact-text">{lead.phone}</span>
                            </div>
                          )}
                        </div>

                        <div className="prospect-details">
                          <div className="detail-item">
                            <span className="detail-label">Budget</span>
                            <span className="detail-value">
                              {lead.budgetMin || lead.budgetMax
                                ? `${lead.budgetMin ? '$' + parseFloat(lead.budgetMin).toLocaleString() : ''}${lead.budgetMin && lead.budgetMax ? ' – ' : ''}${lead.budgetMax ? '$' + parseFloat(lead.budgetMax).toLocaleString() : ''}`
                                : 'Not specified'}
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Unit Type</span>
                            <span className="detail-value">{parseUnitType(lead.propertyInterest)}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Source</span>
                            <span className="detail-value">{getSourceIcon(lead.source)} {lead.source}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Last Contact</span>
                            <span className="detail-value">
                              {lead.lastContact ? new Date(lead.lastContact).toLocaleDateString() : '—'}
                            </span>
                          </div>
                        </div>

                        {/* Inline status + quick actions */}
                        <div className="prospect-actions" onClick={(e) => e.stopPropagation()}>
                          <select
                            className="inline-status-select"
                            value={lead.status}
                            onChange={(e) => handleStatusChange(lead.id, e.target.value, e)}
                          >
                            {STATUS_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <button
                            className="action-btn call-btn"
                            title="Call"
                            onClick={() => window.location.href = `tel:${lead.phone}`}
                          >📞</button>
                          <button
                            className="action-btn email-btn"
                            title="Email"
                            onClick={() => window.location.href = `mailto:${lead.email}`}
                          >📧</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* List / Table View */
                  <div className="list-view-wrapper">
                    <table className="crm-table">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <input
                              type="checkbox"
                              onChange={toggleSelectAll}
                              checked={selectedLeads.size === paginatedLeads.length && paginatedLeads.length > 0}
                            />
                          </th>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Source</th>
                          <th>Budget</th>
                          <th>Unit Type</th>
                          <th>Score</th>
                          <th>Last Contact</th>
                          <th style={{ width: 80 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedLeads.map((lead) => (
                          <tr
                            key={lead.id}
                            className="crm-table-row"
                            onClick={() => navigate(`/applicant/${lead.id}`)}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedLeads.has(lead.id)}
                                onChange={(e) => toggleSelect(lead.id, e)}
                              />
                            </td>
                            <td>
                              <div className="table-name-cell">
                                <div className="prospect-avatar small">
                                  {getInitials(lead.firstName, lead.lastName)}
                                </div>
                                <div>
                                  <div className="table-name">{lead.firstName} {lead.lastName}</div>
                                  <div className="table-email">{lead.email}</div>
                                </div>
                              </div>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <select
                                className="inline-status-select"
                                value={lead.status}
                                onChange={(e) => handleStatusChange(lead.id, e.target.value, e)}
                              >
                                {STATUS_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </td>
                            <td>{getSourceIcon(lead.source)} {lead.source}</td>
                            <td>
                              {lead.budgetMin || lead.budgetMax
                                ? `${lead.budgetMin ? '$' + parseFloat(lead.budgetMin).toLocaleString() : ''}${lead.budgetMin && lead.budgetMax ? '–' : ''}${lead.budgetMax ? '$' + parseFloat(lead.budgetMax).toLocaleString() : ''}`
                                : '—'}
                            </td>
                            <td>{parseUnitType(lead.propertyInterest)}</td>
                            <td>
                              <div className="table-score-cell">
                                <div className="score-bar-track small">
                                  <div
                                    className="score-bar-fill"
                                    style={{ width: `${lead.score}%`, background: getScoreColor(lead.score) }}
                                  />
                                </div>
                                <span style={{ color: getScoreColor(lead.score), fontWeight: 600, fontSize: '0.8rem' }}>
                                  {lead.score}
                                </span>
                              </div>
                            </td>
                            <td>{lead.lastContact ? new Date(lead.lastContact).toLocaleDateString() : '—'}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  className="action-btn call-btn"
                                  title="Call"
                                  onClick={() => window.location.href = `tel:${lead.phone}`}
                                >📞</button>
                                <button
                                  className="action-btn email-btn"
                                  title="Email"
                                  onClick={() => window.location.href = `mailto:${lead.email}`}
                                >📧</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* ── Pipeline (Kanban) ─────────────────────────────────────────── */}
            {activeTab === 'pipeline' && (
              <div className="kanban-board">
                {pipeline.map((stage, idx) => (
                  <div key={idx} className="kanban-column">
                    <div className="kanban-column-header" style={{ borderTop: `4px solid ${stage.color}` }}>
                      <span className="kanban-stage-name">{stage.stage}</span>
                      <span className="kanban-count" style={{ background: stage.color }}>{stage.count}</span>
                    </div>
                    <div className="kanban-value">{stage.value} est.</div>
                    <div className="kanban-cards">
                      {stage.leads.length === 0 ? (
                        <div className="kanban-empty">No leads in this stage</div>
                      ) : (
                        stage.leads.map((lead) => (
                          <div
                            key={lead.id}
                            className="kanban-card"
                            onClick={() => navigate(`/applicant/${lead.id}`)}
                          >
                            <div className="kanban-card-name">{lead.firstName} {lead.lastName}</div>
                            <div className="kanban-card-meta">
                              <span className="kanban-card-unit">{parseUnitType(lead.propertyInterest)}</span>
                              <span className="kanban-card-score" style={{ color: getScoreColor(lead.score) }}>
                                ★ {lead.score}
                              </span>
                            </div>
                            {(lead.budgetMin || lead.budgetMax) && (
                              <div className="kanban-card-budget">
                                {lead.budgetMax
                                  ? `$${parseFloat(lead.budgetMax).toLocaleString()}/mo`
                                  : `$${parseFloat(lead.budgetMin).toLocaleString()}/mo`}
                              </div>
                            )}
                            {lead.source && (
                              <div className="kanban-card-source">
                                {getSourceIcon(lead.source)} {lead.source}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Activity Feed ─────────────────────────────────────────────── */}
            {activeTab === 'activity' && (
              <div className="activity-view">
                <div className="activity-header-bar">
                  <h3 style={{ margin: 0, color: '#1e293b' }}>Activity Feed</h3>
                  <button className="add-contact-btn" onClick={() => setShowLogActivity(true)}>
                    + Log Activity
                  </button>
                </div>
                {activities.length === 0 ? (
                  <div className="empty-state">
                    <p>No activities yet. Use "Log Activity" to record calls, emails, and notes.</p>
                  </div>
                ) : (
                  <div className="activities-list">
                    {activities.map((activity) => (
                      <div
                        key={activity.id}
                        className="activity-item"
                        onClick={() => navigate(`/applicant/${activity.leadId}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="activity-icon">{getActivityIcon(activity.type)}</div>
                        <div className="activity-content">
                          <div className="activity-description">{activity.description}</div>
                          <div className="activity-meta">
                            <span className="activity-contact">{activity.contact}</span>
                            <span className="activity-date">
                              {activity.date ? new Date(activity.date).toLocaleDateString() : ''}
                            </span>
                          </div>
                        </div>
                        <div className="activity-status">
                          <span className="status-badge completed">{activity.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ── Pagination ────────────────────────────────────────────────────── */}
          {activeTab === 'prospects' && totalPages > 1 && (
            <div className="crm-pagination">
              <div className="pagination-left">
                <label>Rows per page:</label>
                <select
                  className="rows-per-page-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  Showing {startIndex + 1}–{Math.min(startIndex + rowsPerPage, filteredLeads.length)} of {filteredLeads.length}
                </span>
              </div>
              <div className="pagination-right">
                <button className="btn-page" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Previous</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p = totalPages <= 5 ? i + 1
                    : currentPage <= 3 ? i + 1
                    : currentPage >= totalPages - 2 ? totalPages - 4 + i
                    : currentPage - 2 + i;
                  return (
                    <button key={p} className={`btn-page ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>
                      {p}
                    </button>
                  );
                })}
                <button className="btn-page" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Add Lead Modal ──────────────────────────────────────────────────── */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Add New Lead</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" value={newLead.firstName} onChange={(e) => setNewLead({ ...newLead, firstName: e.target.value })} placeholder="First name" />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" value={newLead.lastName} onChange={(e) => setNewLead({ ...newLead, lastName: e.target.value })} placeholder="Last name" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} placeholder="Email address" />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} placeholder="Phone number" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Source</label>
                  <select value={newLead.source} onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}>
                    {SOURCE_OPTIONS.filter((s) => s !== 'All').map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={newLead.status} onChange={(e) => setNewLead({ ...newLead, status: e.target.value })}>
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Property Interest</label>
                <input type="text" value={newLead.propertyInterest} onChange={(e) => setNewLead({ ...newLead, propertyInterest: e.target.value })} placeholder="e.g. 2 bedroom downtown, pet-friendly" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Budget Min ($)</label>
                  <input type="number" value={newLead.budgetMin} onChange={(e) => setNewLead({ ...newLead, budgetMin: e.target.value })} placeholder="Minimum" />
                </div>
                <div className="form-group">
                  <label>Budget Max ($)</label>
                  <input type="number" value={newLead.budgetMax} onChange={(e) => setNewLead({ ...newLead, budgetMax: e.target.value })} placeholder="Maximum" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Move-in Date</label>
                  <input type="date" value={newLead.moveInDate} onChange={(e) => setNewLead({ ...newLead, moveInDate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Lead Score: {newLead.score}/100</label>
                  <input type="range" min="0" max="100" value={newLead.score} onChange={(e) => setNewLead({ ...newLead, score: parseInt(e.target.value) })} style={{ width: '100%', marginTop: 8 }} />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={newLead.notes} onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })} placeholder="Initial notes about this lead..." rows="3" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleAddLead} disabled={!newLead.firstName || !newLead.email}>Add Lead</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Log Activity Modal ──────────────────────────────────────────────── */}
      {showLogActivity && (
        <div className="modal-overlay" onClick={() => setShowLogActivity(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>Log Activity</h2>
              <button className="modal-close" onClick={() => setShowLogActivity(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Lead *</label>
                <select
                  value={logActivity.leadId}
                  onChange={(e) => setLogActivity({ ...logActivity, leadId: e.target.value })}
                >
                  <option value="">Select a lead…</option>
                  {leads.map((l) => (
                    <option key={l.id} value={l.id}>{l.firstName} {l.lastName}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Activity Type</label>
                  <select value={logActivity.type} onChange={(e) => setLogActivity({ ...logActivity, type: e.target.value })}>
                    {ACTIVITY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={logActivity.date} onChange={(e) => setLogActivity({ ...logActivity, date: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={logActivity.description}
                  onChange={(e) => setLogActivity({ ...logActivity, description: e.target.value })}
                  placeholder="What happened? e.g. Called and discussed 2BR budget, very interested."
                  rows="4"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowLogActivity(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleLogActivity}
                disabled={!logActivity.leadId || !logActivity.description}
              >
                Log Activity
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
